import { useMemo, useState } from "react";
import { actualizar, eliminarVarias, rpc } from "../../services/datos";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";

const input = "campo";
const label = "etiqueta";

const fechaCorta = (fecha) => (fecha ? new Date(`${fecha}T00:00:00`).toLocaleDateString("es-CO") : "");

// Periodos para ver varias reuniones a la vez (días hacia adelante desde hoy).
export const PERIODOS = [["7", "Próximos 7 días"], ["14", "Próximos 14 días"], ["30", "Próximos 30 días"], ["todo", "Todo lo programado"]];

const hoyISO = () => new Date().toLocaleDateString("en-CA"); // AAAA-MM-DD en la zona local

const sumarDias = (fecha, dias) => {
  const d = new Date(`${fecha}T00:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toLocaleDateString("en-CA");
};

const reunionesProximas = (servicios, hoy) =>
  servicios.filter((s) => s.fecha >= hoy)
    .sort((a, b) => `${a.fecha}${a.hora || ""}`.localeCompare(`${b.fecha}${b.hora || ""}`));

export function estadoTurno(turno) {
  if (turno.rechazado) return { texto: "Rechazado", clase: "border border-rose-500/30 bg-rose-500/15 text-rose-300" };
  if (turno.confirmado) return { texto: "Confirmado", clase: "border border-emerald-500/30 bg-emerald-500/15 text-emerald-300" };
  return { texto: "Pendiente", clase: "border border-zinc-500/30 bg-zinc-500/15 text-zinc-400" };
}

// Pestaña Turnos de Servidores: asigna un turno a varios servidores a la vez y
// permite eliminar varios turnos de una sola vez (cada servidor recibe un aviso).
// Se abre en la próxima reunión (lista para asignar); con "Varias reuniones" se ven los
// turnos de un periodo, agrupados por área en desplegables, para no mostrar todo lo futuro.
function PanelTurnos({ servicios, areas, miembros, miembrosArea, turnos, onCambio, servicioInicial }) {
  const hoy = hoyISO();
  const proximas = useMemo(() => reunionesProximas(servicios, hoy), [servicios, hoy]);
  const [servicioId, setServicioId] = useState(() => {
    if (servicioInicial) return String(servicioInicial);
    const primera = reunionesProximas(servicios, hoyISO())[0];
    return primera ? String(primera.id) : "";
  });
  const [periodo, setPeriodo] = useState("14");
  const [areaId, setAreaId] = useState("");
  const [elegidos, setElegidos] = useState(new Set());
  const [rol, setRol] = useState("");
  const [notas, setNotas] = useState("");
  const [seleccionTurnos, setSeleccionTurnos] = useState(new Set());
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState("");

  const habilitados = useMemo(() => {
    if (!areaId) return [];
    const ids = new Set(
      miembrosArea.filter((ma) => ma.area_id === Number(areaId) && ma.activo !== false).map((ma) => ma.miembro_id)
    );
    return miembros.filter((m) => ids.has(m.id));
  }, [areaId, miembros, miembrosArea]);

  const yaAsignados = useMemo(
    () => new Set(
      turnos
        .filter((t) => t.servicio_id === Number(servicioId) && t.area_id === Number(areaId))
        .map((t) => t.miembro_id)
    ),
    [turnos, servicioId, areaId]
  );

  const limite = periodo === "todo" ? null : sumarDias(hoy, Number(periodo));
  const visibles = servicioId
    ? turnos.filter((t) => t.servicio_id === Number(servicioId))
    : turnos.filter((t) => {
      const fecha = t.servicios?.fecha || "";
      return fecha >= hoy && (!limite || fecha <= limite);
    });

  // Turnos agrupados por área (desplegables), cada grupo en orden de fecha.
  const grupos = useMemo(() => {
    const porArea = visibles.reduce((acc, t) => {
      const nombre = t.areas_servicio?.nombre || "Sin área";
      (acc[nombre] = acc[nombre] || []).push(t);
      return acc;
    }, {});
    const clave = (t) => `${t.servicios?.fecha || ""}${t.servicios?.hora || ""}${t.miembros?.nombres || ""}`;
    return Object.entries(porArea)
      .map(([nombre, lista]) => ({ nombre, turnos: [...lista].sort((a, b) => clave(a).localeCompare(clave(b))) }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [visibles]);

  // Una sola reunión tiene pocas áreas: se abren todas. En un periodo empiezan cerradas.
  const [alternadas, setAlternadas] = useState(new Set());
  const estaAbierta = (nombre) => Boolean(servicioId) !== alternadas.has(nombre);
  const alternarArea = (nombre) => alternar(alternadas, setAlternadas, nombre);

  const alternar = (conjunto, setConjunto, id) => {
    const nuevo = new Set(conjunto);
    nuevo.has(id) ? nuevo.delete(id) : nuevo.add(id);
    setConjunto(nuevo);
  };

  const disponibles = habilitados.filter((m) => !yaAsignados.has(m.id));
  const todosElegidos = disponibles.length > 0 && disponibles.every((m) => elegidos.has(m.id));
  const todosLosTurnos = visibles.length > 0 && visibles.every((t) => seleccionTurnos.has(t.id));

  const ejecutar = async (accion) => {
    setOcupado(true);
    setMensaje("");
    try {
      await accion();
      onCambio();
    } catch (error) {
      setMensaje(error.message || "No se pudo completar la acción");
    } finally {
      setOcupado(false);
    }
  };

  const crear = () => ejecutar(async () => {
    const resultado = await rpc("asignar_turnos", {
      p_servicio: Number(servicioId),
      p_area: Number(areaId),
      p_miembros: [...elegidos],
      p_rol: rol,
      p_notas: notas,
    });
    setElegidos(new Set());
    setRol("");
    setNotas("");
    const omitidos = resultado.omitidos?.length ? ` (${resultado.omitidos.length} ya tenían este turno)` : "";
    setMensaje(`Se asignaron ${resultado.creados.length} turno(s)${omitidos}.`);
  });

  const eliminar = async (ids) => {
    const texto = ids.length === 1 ? "¿Eliminar este turno?" : `¿Eliminar los ${ids.length} turnos seleccionados?`;
    if (!(await confirmar(`${texto} Se avisará a quienes ya conocían su turno.`, { aceptar: "Eliminar" }))) return;
    ejecutar(async () => {
      await eliminarVarias("turnos_servicio", ids);
      setSeleccionTurnos(new Set());
      setMensaje(`Se eliminaron ${ids.length} turno(s).`);
    });
  };

  const rotar = () => ejecutar(async () => {
    const resultado = await rpc("rotacion_areas", { p_servicio: Number(servicioId), p_asignar: true });
    const incompletas = resultado.areas.filter((a) => a.sin_cubrir > 0).map((a) => a.area.nombre);
    const partes = [
      resultado.areas.length === 0
        ? "Ninguna área con rotación automática se usa en este tipo de reunión."
        : `Rotación aplicada: ${resultado.creados.length} turno(s) nuevo(s). Cada servidor recibe el aviso 7 días antes y un recordatorio el día anterior.`,
    ];
    if (incompletas.length) partes.push(`Faltan servidores en: ${incompletas.join(", ")}.`);
    setMensaje(partes.join(" "));
  });

  const cambiarConfirmado = (turno) =>
    ejecutar(() => actualizar("turnos_servicio", turno.id, { confirmado: !turno.confirmado }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="panel p-4">
        <h3 className="titulo-panel mb-4">Servicio</h3>
        <Selector aria-label="Reunión" value={servicioId} className={input}
          onChange={(e) => { setServicioId(e.target.value); setSeleccionTurnos(new Set()); setAlternadas(new Set()); }}>
          <option value="">Varias reuniones (por periodo)</option>
          {proximas.map((s) => (
            <option key={s.id} value={s.id}>{s.fecha === hoy ? "Hoy" : fechaCorta(s.fecha)} - {s.tipos_servicio?.nombre || s.titulo || "Servicio"}</option>
          ))}
          {servicioId && !proximas.some((s) => String(s.id) === servicioId) && (
            <option value={servicioId}>Reunión elegida</option>
          )}
        </Selector>

        {servicioId && (
          <button onClick={rotar} disabled={ocupado}
            className="w-full mt-3 btn-fantasma btn-sm">
            Aplicar rotación automática
          </button>
        )}

        <div className="mt-4">
          <label className={label}>Área</label>
          <Selector value={areaId} onChange={(e) => { setAreaId(e.target.value); setElegidos(new Set()); }} className={input}>
            <option value="">Seleccionar área</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </Selector>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className={label}>Servidores del área</span>
            {disponibles.length > 1 && (
              <button
                type="button"
                onClick={() => setElegidos(todosElegidos ? new Set() : new Set(disponibles.map((m) => m.id)))}
                className="text-dorado text-xs hover:underline"
              >
                {todosElegidos ? "Quitar todos" : "Seleccionar todos"}
              </button>
            )}
          </div>
          {!areaId ? (
            <p className="text-gray-400 text-sm mt-2">Primero selecciona un área.</p>
          ) : habilitados.length === 0 ? (
            <p className="text-red-400 text-sm mt-2">Esta área no tiene servidores activos asignados.</p>
          ) : (
            <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-gray-700 divide-y divide-gray-700">
              {habilitados.map((m) => {
                const asignado = servicioId && yaAsignados.has(m.id);
                return (
                  <label key={m.id} className={`flex items-center gap-3 p-3 text-sm ${asignado ? "opacity-50" : "cursor-pointer hover:bg-oscuro"}`}>
                    <input
                      type="checkbox"
                      disabled={Boolean(asignado)}
                      checked={elegidos.has(m.id)}
                      onChange={() => alternar(elegidos, setElegidos, m.id)}
                      className="accent-yellow-500"
                    />
                    <span className="text-white">{m.nombres} {m.apellidos}</span>
                    {asignado && <span className="ml-auto text-xs text-gray-400">Ya asignado</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className={label}>Rol en el turno</label>
          <input value={rol} onChange={(e) => setRol(e.target.value)} className={input} placeholder="Ej: Sonido, líder, apoyo" />
        </div>
        <div className="mt-4">
          <label className={label}>Lo que le toca hacer</label>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)} className={input} rows={4}
            placeholder="Escribe la descripción o instrucciones del turno" />
        </div>
        <button
          onClick={crear}
          disabled={ocupado || !servicioId || !areaId || elegidos.size === 0}
          className="w-full mt-4 btn-primario"
        >
          {elegidos.size > 1 ? `Crear ${elegidos.size} turnos` : "Crear turno"}
        </button>
        {mensaje && <p className="text-sky-200 text-sm mt-3">{mensaje}</p>}
      </div>

      <div className="panel lg:col-span-2 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <h3 className="titulo-panel">Turnos programados</h3>
            {!servicioId && (
              <Selector aria-label="Periodo" value={periodo} className={`${input} sm:w-48 py-2`}
                onChange={(e) => { setPeriodo(e.target.value); setSeleccionTurnos(new Set()); }}>
                {PERIODOS.map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
              </Selector>
            )}
          </div>
          {visibles.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={todosLosTurnos}
                  onChange={() => setSeleccionTurnos(todosLosTurnos ? new Set() : new Set(visibles.map((t) => t.id)))}
                  className="accent-yellow-500"
                />
                Seleccionar todos
              </label>
              <button
                onClick={() => eliminar([...seleccionTurnos])}
                disabled={ocupado || seleccionTurnos.size === 0}
                className="btn-peligro btn-sm"
              >
                Eliminar seleccionados{seleccionTurnos.size ? ` (${seleccionTurnos.size})` : ""}
              </button>
            </div>
          )}
        </div>

        {visibles.length === 0 ? (
          <p className="text-gray-400 text-sm">
            {servicioId ? "Esta reunión aún no tiene turnos. Asígnalos a la izquierda." : "No hay turnos en este periodo."}
          </p>
        ) : (
          <div className="space-y-3">
            {grupos.map((grupo) => {
              const abierta = estaAbierta(grupo.nombre);
              const confirmados = grupo.turnos.filter((t) => t.confirmado && !t.rechazado).length;
              const noPueden = grupo.turnos.filter((t) => t.rechazado).length;
              const sinResponder = grupo.turnos.length - confirmados - noPueden;
              return (
                <section key={grupo.nombre} className="rounded-xl border border-gray-700 bg-oscuro/50 overflow-hidden">
                  <button type="button" onClick={() => alternarArea(grupo.nombre)} aria-expanded={abierta}
                    className="w-full px-4 py-3 flex flex-col gap-1 text-left transition hover:bg-oscuro sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      <span className="text-white font-bold">{grupo.nombre}</span>
                      <span className="text-gray-400 text-xs ml-2">{grupo.turnos.length} turno(s)</span>
                    </span>
                    <span className="flex items-center gap-3 text-xs">
                      <span className="text-green-300">{confirmados} confirmados</span>
                      <span className="text-yellow-300">{sinResponder} sin responder</span>
                      {noPueden > 0 && <span className="text-red-300">{noPueden} no pueden</span>}
                      <span className="text-gray-400" aria-hidden="true">{abierta ? "▲" : "▼"}</span>
                    </span>
                  </button>
                  {abierta && (
                    <div className="border-t border-gray-700 p-3 space-y-2">
            {grupo.turnos.map((t) => {
              const estado = estadoTurno(t);
              return (
                <div key={t.id} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${seleccionTurnos.has(t.id) ? "border-dorado" : "border-gray-700"}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={seleccionTurnos.has(t.id)} onChange={() => alternar(seleccionTurnos, setSeleccionTurnos, t.id)}
                      className="mt-1 accent-yellow-500" />
                    <span>
                      <span className="block text-white text-sm font-semibold">{t.miembros?.nombres} {t.miembros?.apellidos}</span>
                      <span className="block text-gray-400 text-xs">
                        {t.areas_servicio?.nombre} · {t.rol_en_turno || "Servidor"} · {fechaCorta(t.servicios?.fecha)}
                      </span>
                      {t.rechazado && (
                        <span className="block text-red-300 text-xs mt-1">Motivo: {t.motivo_rechazo || "No indicó un motivo"}</span>
                      )}
                    </span>
                  </label>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => cambiarConfirmado(t)} disabled={ocupado} className={`px-3 py-1 rounded-lg text-xs ${estado.clase}`}>
                      {estado.texto}
                    </button>
                    <button onClick={() => eliminar([t.id])} disabled={ocupado} className="text-red-400 text-sm hover:text-red-300">Eliminar</button>
                  </div>
                </div>
              );
            })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PanelTurnos;
